/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
  message,
  TreeSelect,
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';

import {
  getRegistrosContablesPercepciones,
  postRegistroContablePercepcion,
  deleteRegistroContablePercepcion,
  patchRegistroContablePercepcion,
  getRegistroContablePercepcion,
  autorizarRegistroContablePercepcion,
} from 'api/RegistroContablePercepciones';

import { checkToken } from 'api/utils';
import {
  ESTATUS_ACTIVO,
  ESTATUS_INACTIVO,
  ESTATUS_AUTORIZADO,
} from 'utils/constants';
import { getAreas, getCuentasContables, getEstadosGlobales } from 'api/catalogos';
import { getTiposDeEmpleadosPresupuestales } from 'api/catalogos/tiposDeEmpleadosPresupuestales';
import makeTreeFromCuentasContables from 'utils/makeTreeFromCuentasContables';

export const registroContablePercepcionesPermission = {
  permissionModel: 'registrocontablepercepcion',
  catalogsModels: [],
  tabsModels: [],
};

const RegistroContablePercepciones = ({ permission, percepcionId, initialTipoEmpleado }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [visibleConfirmInactivo, setVisibleConfirmInactivo] = useState(false);
  const [disableFields, setDisableFields] = useState(false);
  const [tiposEmpleado, setTiposEmpleado] = useState([]);

  // catálogos locales
  const [areas, setAreas] = useState([]);
  const [cuentasContables, setCuentasContables] = useState([]);

  const formInstances = [form];

  // Construye árbol de Áreas
  const normalizeDataAreas = (_areasFetchArray) => {
    const mainMap = new Map();
    _areasFetchArray.forEach((item) => {
      mainMap.set(item.id, { ...item, children: [] });
    });

    const result = [];
    _areasFetchArray.forEach((item) => {
      if (item.directorio_area_padre) {
        const parent = mainMap.get(item.directorio_area_padre);
        if (parent) parent.children.push(mainMap.get(item.id));
      } else {
        result.push(mainMap.get(item.id));
      }
    });

    result.sort((a, b) => a.clave.localeCompare(b.clave));
    return result;
  };

  // Esta función adapta el árbol para que funcione con TreeSelect
  const formatTreeSelectData = (nodes) => nodes.map((node) => ({
    title: `${node.clave} - ${node.nombre.trim()}`,
    value: node.id,
    children: node.children ? formatTreeSelectData(node.children) : [],
  }));
  // Construye árbol de cuentas contables
  const normalizeCuentasContables = (_data) => {
    if (_data) {
      return _data.map((item) => {
        const clone = { ...item };
        const children = normalizeCuentasContables(clone.children);

        //  Solo es seleccionable si es tipo === 7 (afectable)
        const isAfectable = clone.tipo === 7;

        // Si no tiene hijos, lo evaluamos como hoja
        const isLeaf = !children.length;

        //  Solo habilitamos selección si es hoja y afectable
        const selectable = isLeaf && isAfectable;

        if (!isLeaf) {
          delete clone.children;
        }

        return {
          ...clone,
          title: `${clone.clave_visible || clone.clave} - ${clone.nombre}`,
          value: clone.id,
          key: clone.id,
          children: children.length ? children : undefined,
          disabled: !selectable, //  Solo se puede seleccionar si cumple la condición
        };
      });
    }
    return [];
  };

  const findInTree = (nodes, id) => {
    if (!Array.isArray(nodes)) return null;

    return nodes.reduce((found, node) => {
      if (found) return found;

      if (node.id === id) {
        return node;
      }

      if (node.children) {
        return findInTree(node.children, id) || found;
      }

      return found;
    }, null);
  };

  const fetchData = async () => {
    setLoading(true);
    const params = {};
    if (percepcionId) params.percepcion = percepcionId;
    const _data = await getRegistrosContablesPercepciones(params);
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _data,
        _areas,
        _cuentasContables,
        _tiposEmpleado,
      ] = await Promise.all([
        getEstadosGlobales(),
        getRegistrosContablesPercepciones(percepcionId ? { percepcion: percepcionId } : {}),
        getAreas({ estados_globales: ESTATUS_AUTORIZADO }),
        getCuentasContables(),
        getTiposDeEmpleadosPresupuestales(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setData(formatReceived(_data));
        const formattedURS = formatTreeSelectData(normalizeDataAreas(_areas));
        setAreas(formattedURS);
        // eslint-disable-next-line max-len
        const normalizedCuentas = normalizeCuentasContables(makeTreeFromCuentasContables(_cuentasContables));
        setCuentasContables(normalizedCuentas);
        setTiposEmpleado(_tiposEmpleado);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, [percepcionId]);

  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    setDisableFields(false);
    setSelectedRowKeys([]);
    setSelectedData({});
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    if (!([1].includes(key))) {
      const match = responseData || data.find((item) => item.id === key);
      const _item = await getRegistroContablePercepcion(match.id);
      setTimeout(() => {
        if (_item) {
          form.resetFields();
          setTimeout(() => {
            form.setFieldsValue(formatReceived(_item));
            setDisableFields(_item?.estados_globales !== ESTATUS_ACTIVO);
            setVisible(true);
          });
        }
      });
    } else {
      message.warn('Este campo no es editable');
    }
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      if (percepcionId) values.percepcion = percepcionId;
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchRegistroContablePercepcion(key, values) : postRegistroContablePercepcion(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, formInstances);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteRegistroContablePercepcion(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData({ ...record });
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    form.resetFields();
    if (initialTipoEmpleado) {
      form.setFieldValue('tipo_empleado_presupuestal', initialTipoEmpleado);
    }
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    const [key] = selectedRowKeys;
    if (key) {
      setVisibleAlert(true);
    } else {
      setVisibleAlert(false);
      message.warn('Este campo no se puede borrar');
    }
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar un registro ya autorizado');
        return;
      }
      const saved = await autorizarRegistroContablePercepcion(key);
      if (saved) {
        // eslint-disable-next-line max-len
        const _data = await getRegistrosContablesPercepciones(percepcionId ? { percepcion: percepcionId } : {});
        setData(formatReceived(_data));
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const columns = [
    {
      titleText: 'Tipo Empleado',
      dataIndex: 'tipo_empleado_presupuestal',
      key: 'tipo_empleado_presupuestal',
      width: 220,
      render: (val) => tiposEmpleado.find((t) => t.id === val)?.descripcion || val,
    },
    {
      titleText: 'Área',
      dataIndex: 'directorio_de_area_nombre',
      key: 'directorio_de_area_nombre',
      width: 300,
      // eslint-disable-next-line max-len
      render: (val, record) => record.directorio_de_area_nombre || areas.find((a) => a.id === record.directorio_de_area)?.nombre || val,

    },
    {
      titleText: 'Cuenta Contable',
      dataIndex: 'cuenta_contable',
      key: 'cuenta_contable',
      width: 350,
      render: (val) => {
        const _cuentasContables = findInTree(cuentasContables, val);

        return _cuentasContables
          ? `${_cuentasContables.clave_visible || _cuentasContables.clave} - ${_cuentasContables.nombre}`
          : val;
      },
    },
    {
      titleText: 'Registro Adicional',
      dataIndex: 'registro_adicional',
      key: 'registro_adicional',
      width: 150,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 150,
      render: (val) => val?.descripcion
  || estados.find((e) => e.id === val)?.descripcion
  || '',
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          customActions={[
            {
              onClick: () => setVisibleAutorizar(true),
              permission: { model: 'registrocontablepercepcion', action: 'autorizar' },
              icon: <CheckCircleOutlined />,
              text: 'Autorizar',
              disabledBySelection: true,
              disabled: selectedData?.estados_globales !== ESTATUS_ACTIVO,
              className: 'success',
            },
          ]}
          disableDelete={selectedData?.estados_globales === ESTATUS_AUTORIZADO}
          permission={permission}
          allowExportPDF={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Registro Contable`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!disableFields}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="registro_contable_percepciones"
            initialValues={{
              estados_globales: ESTATUS_ACTIVO,
            }}
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="directorio_de_area"
                label="Área"
                rules={[requiredRule]}
                hasFeedback
              >
                <TreeSelect
                  showSearch
                  treeNodeFilterProp="title"
                  treeData={areas}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="cuenta_contable"
                label="Cuenta Contable del Gasto de la Percepción o Reembolso"
                hasFeedback
              >

                <TreeSelect
                  keyProp="clave"
                  labelProp="nombre"
                  showSearch
                  treeNodeFilterProp="title"
                  treeData={cuentasContables}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="registro_adicional"
                label="Registro Adicional"
                hasFeedback
              >
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="tipo_empleado_presupuestal"
                label="Tipo de Empleado Presupuestal"
                rules={[requiredRule]}
                hasFeedback
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  dataSource={tiposEmpleado}
                  disabled={!!initialTipoEmpleado}
                />
              </Form.Item>
            </Col>

            <Form.Item
              name="vigencia_final"
              label="Vigencia Final"
            >
              <Input type="date" />
            </Form.Item>

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="estados_globales"
                label="Estatus"
                rules={[requiredRule]}
                hasFeedback
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  dataSource={estados}
                  disabled={selectedRowKeys.length <= 0 || disableFields}
                  onChange={(val) => setVisibleConfirmInactivo(val === ESTATUS_INACTIVO)}
                />
              </Form.Item>
            </Col>

            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>

        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Registro Contable ${form.getFieldValue('registro_adicional') || ''}`}
          loading={loading}
        />

        <ModalConfirmation
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => setVisibleAutorizar(false)}
          contentTitle={`Autorizar el Registro Contable ${selectedData?.id}`}
          open={visibleAutorizar}
          loading={loading}
        />

        <ModalConfirmation
          onFinish={() => setVisibleConfirmInactivo(false)}
          ConfirmInactive
          onCancel={() => {
            form.setFieldValue('estados_globales', ESTATUS_ACTIVO);
            setVisibleConfirmInactivo(false);
          }}
          open={visibleConfirmInactivo}
          contentTitle={`Inactivar el Registro Contable ${selectedData?.id}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default RegistroContablePercepciones;
